import React, { useMemo } from 'react';
import { BarChart } from '@aidkitorg/component-library';
import { SupportedDashboardComponent, TranslatedComponentResult } from '@aidkitorg/types/lib/admin-panel/dashboard';

type AdminPanelBarChartProps = {
  component: SupportedDashboardComponent & { kind: 'Bar Chart' };
  queryResult: TranslatedComponentResult;
  height?: number;
  tableCaption?: string;
};

export const AdminPanelBarChart: React.FC<AdminPanelBarChartProps> = ({
  component,
  queryResult,
  height = 300,
  tableCaption,
}) => {
  const { processedData, categoryKey, valueKey } = useMemo(() => {
    const primaryGroupField = component.groups[0].kind === 'Field' ? component.groups[0].field : '';
    const summaryKey = queryResult.summaryKey || '';

    // Check if we have grouped bars (secondary field in bars config)
    const barsConfig = (component as any).bars;
    const hasSecondaryGrouping = barsConfig && barsConfig.kind === 'Grouped' && barsConfig.values?.[0]?.field;

    // Convert string values to numbers for proper Y-axis scaling
    let processedData = queryResult.data.map((item: Record<string, any>) => ({
      ...item,
      [summaryKey]: Number(item[summaryKey])
    }));
    let valueKeys = [summaryKey]; // Default to original summaryKey for single group

    if (hasSecondaryGrouping) {
      const secondaryField = barsConfig.values[0].field;

      // Group data by primary field and pivot secondary field
      const groupedData: Record<string, Record<string, any>> = {};
      const secondaryValueSet = new Set<string>();

      queryResult.data.forEach((row: any) => {
        const primaryValue = String(row[primaryGroupField] || 'Unknown');
        const secondaryValue = String(row[secondaryField] || 'null');
        const aggregateValue = Number(row[summaryKey]) || 0;

        if (!groupedData[primaryValue]) {
          groupedData[primaryValue] = { [primaryGroupField]: primaryValue };
        }

        secondaryValueSet.add(secondaryValue);

        if (!groupedData[primaryValue][secondaryValue]) {
          groupedData[primaryValue][secondaryValue] = 0;
        }
        groupedData[primaryValue][secondaryValue] += aggregateValue;
      });

      processedData = Object.values(groupedData);
      valueKeys = Array.from(secondaryValueSet); // Use secondary field values as value keys
    }

    return {
      processedData,
      categoryKey: primaryGroupField,
      valueKey: valueKeys.length === 1 ? valueKeys[0] : valueKeys,
    };
  }, [component, queryResult]);

  return (
    <BarChart
      data={processedData}
      categoryKey={categoryKey}
      valueKey={valueKey}
      xAxisLabel={component.xAxisLabel}
      yAxisLabel={component.yAxisLabel}
      height={height}
      tableCaption={tableCaption}
    />
  );
};
